export interface HeroData {
  name: string;
  title: string;
  subtitle: string;
  description: string;
}

export interface MyVision {
  name: string;
  descriptionLine1: string;
  descriptionLine2: string;
}

export interface StrategicPriorities {
  title: string;
  descriptionLine1: string;
  descriptionLine2: string;
  priorities: {
    title: string;
    description: string;
    icon: string;
  }[];
}

export interface WhyMe {
  title: string;
  description: string;
  cards: {
    title: string;
    description: string;
    listItems: string[];
    icon: string;
  }[];
}

export interface MyCommitment {
  title: string;
  descriptionLine1: string;
  descriptionLine2: string;
  contact: {
    email: string;
    linkedin: string;
    phone: string;
  };
}

export const heroData: HeroData = {
  name: "Abraham Romero Quesada",
  title: "CTO Candidate",
  subtitle: "Driving the Future of Consulting",
  description: "I build and lead high-performing teams that deliver strategic value. My vision is to help shape Edata Consulting into a recognized leader in the next generation of tech consulting."
};

export const myVision: MyVision = {
  name: "My Vision: Leading the Evolution",
  descriptionLine1: "The roles of software development and developers are fundamentally changing. My priority as CTO will be to define and elevate our position in this new landscape. I believe we can lead this change by transforming how we deliver value to our clients.",
  descriptionLine2: "I see the CTO role as a dual-leadership position: an internal technical leader who builds a world-class engineering culture, and an external strategic leader who engages with clients and translates our technical expertise into tangible business value for clients."
};

export const strategicPriorities: StrategicPriorities = {
  title: "My Strategic Priorities: The First 90 Days",
  descriptionLine1: "My initial focus will be on understanding the business from the ground up and aligning technology with strategic goals. This involves deep dives with the team and close collaboration with the CEO and other C-level executives.",
  descriptionLine2: "My approach is to balance forward-thinking innovation with operational stability. While my key objectives will center on preparing our capabilities for the future, with a strong focus on AI, this evolution will be done thoughtfully, ensuring our current systems remain robust and that the team is an active part of the process.",
  priorities: [
    {
      title: "AI Skill Development",
      description: "Ensure the entire development team acquires the necessary skills to leverage AI tools in their daily workflows.",
      icon: "🎓"
    },
    {
      title: "AI Solution Delivery",
      description: "Cultivate a specialized group within the team capable of developing and deploying sophisticated AI-powered solutions for our clients.",
      icon: "🚀"
    },
    {
      title: "Knowledge Transfer",
      description: "Establish a robust internal framework to ensure that AI knowledge is shared effectively across the organization.",
      icon: "🔄"
    },
    {
      title: "Quality & Security First",
      description: "Guarantee that the integration of AI tools enhances our output without ever compromising the quality or security of our deliverables.",
      icon: "🛡️"
    }
  ]
};

export const whyMe: WhyMe = {
  title: "Why Me? A Track Record of Leadership",
  description: "With over 15 years in software development, including 5 years at a Fortune 5 company, I have a proven history of leading and building elite teams. My experience extends beyond code to defining key internal processes, such as mentorship and continuous learning programs, that create a culture of excellence and growth. This background has equipped me to step into the CTO role and make an immediate impact.",
  cards: [
    {
      title: "Strategic Impact at a Fortune 5 Firm",
      description: "During my 5 years contributing to a Fortune 5 environment, I evolved from a senior contractor to a trusted team leader, demonstrating my ability to deliver value and earn leadership responsibilities in a highly demanding setting.",
      listItems: [
        "Led project execution, guiding development teams of 6-8 engineers to meet strategic goals.",
        "Entrusted with conducting technical interviews to build and scale new engineering teams.",
      ],
      icon: "📈"
    },
    {
      title: "Driving Team Growth & Innovation",
      description: "As a senior figure during my 7-year tenure at The Agile Monkeys, I spearheaded key initiatives to create a culture of continuous improvement and prepare the team for future technological challenges.",
      listItems: [
        "Designed and launched the company-wide mentorship program to develop and retain talent.",
        "I was responsible for creating a specialized AI-focused learning program to upskill the entire technical team.",
      ],
      icon: "💡"
    },
    {
      title: "Knowledge Sharing & Communication",
      description: "I firmly believe that technical leadership involves multiplying knowledge. I have a strong track record of synthesizing complex topics and fostering open communication within teams.",
      listItems: [
        "Regularly delivered internal presentations on new technologies and engineering best practices.",
        "Published multiple articles on a range of technical subjects to share expertise with the wider community.",
      ],
      icon: "📖"
    }
  ]
};

export const myCommitment: MyCommitment = {
  title: "My Commitment to Growth",
  descriptionLine1: "My commitment to growth is twofold. On one hand, I am dedicated to continuous learning to stay at the forefront of technology and market trends. On the other, I recognize that the CTO role is as much about people and strategy as it is about code.",
  descriptionLine2: "That is why I am proactively developing my leadership and strategic management skills, preparing myself to meet both the technical and the human-centric challenges of this position.",
  contact: {
    email: "aromqueu@gmail.com",
    linkedin: "https://www.linkedin.com/in/abraham-romero-quesada-11252823/",
    phone: "+34 646 486 453"
  }
}; 